import { nextRefreshDelayMs, REFRESH_RETRY_DELAYS_MS, shouldRefreshOnWake } from '@shared/core/search';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from 'src/auth';

import {
  type EntitledSearchCredentials,
  fetchSearchCredentials,
  isPermanentCredentialsFailure,
} from './fetchSearchCredentials';

export type { EntitledSearchCredentials } from './fetchSearchCredentials';

export type SearchCredentialsState = 'loading' | 'ready' | 'unavailable';

export interface SearchCredentialsValue {
  readonly credentials: EntitledSearchCredentials | null;
  readonly state: SearchCredentialsState;
  readonly refresh: () => Promise<EntitledSearchCredentials | null>;
}

const UNAVAILABLE_AFTER_FAILURES = 3;
const STEADY_RETRY_MS = 30_000;

const SearchKeyContext = createContext<SearchCredentialsValue>({
  credentials: null,
  state: 'unavailable',
  refresh: () => Promise.resolve(null),
});

export interface SearchKeyProviderProps extends PropsWithChildren {
  readonly fetchCredentials?: (getToken: () => Promise<string | null>) => Promise<EntitledSearchCredentials>;
}

export function SearchKeyProvider({ children, fetchCredentials = fetchSearchCredentials }: SearchKeyProviderProps) {
  const { currentUser, getToken } = useAuth();
  const identity = currentUser ? `${currentUser.id}:${currentUser.organizationId ?? ''}` : null;

  const getTokenRef = useRef(getToken);
  const fetchRef = useRef(fetchCredentials);
  useEffect(() => {
    getTokenRef.current = getToken;
    fetchRef.current = fetchCredentials;
  });

  const [credentials, setCredentials] = useState<EntitledSearchCredentials | null>(null);
  const [state, setState] = useState<SearchCredentialsState>('loading');

  const loadRef = useRef<() => Promise<EntitledSearchCredentials | null>>(() => Promise.resolve(null));
  const refresh = useCallback(() => loadRef.current(), []);

  useEffect(() => {
    setCredentials(null);
    if (!identity) {
      setState('unavailable');
      loadRef.current = () => Promise.resolve(null);
      return;
    }
    setState('loading');

    let stopped = false;
    let failures = 0;
    let expiresAt: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight: Promise<EntitledSearchCredentials | null> | null = null;

    const schedule = (delayMs: number) => {
      clearTimeout(timer);
      if (!stopped) timer = setTimeout(() => void load(), delayMs);
    };

    const attempt = async (): Promise<EntitledSearchCredentials | null> => {
      try {
        const next = await fetchRef.current(() => getTokenRef.current());
        if (stopped) return null;
        failures = 0;
        expiresAt = next.expiresAt;
        setCredentials(next);
        setState('ready');
        schedule(nextRefreshDelayMs(next.expiresAt, Date.now(), Math.random));
        return next;
      } catch (error) {
        if (stopped) return null;
        if (isPermanentCredentialsFailure(error)) {
          expiresAt = null;
          setCredentials(null);
          setState('unavailable');
          return null;
        }
        failures += 1;
        if (failures >= UNAVAILABLE_AFTER_FAILURES) {
          expiresAt = null;
          setCredentials(null);
          setState('unavailable');
        }
        schedule(REFRESH_RETRY_DELAYS_MS[failures - 1] ?? STEADY_RETRY_MS);
        return null;
      }
    };

    const load = (): Promise<EntitledSearchCredentials | null> => {
      inFlight ??= attempt().finally(() => {
        inFlight = null;
      });
      return inFlight;
    };

    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      if (expiresAt === null || shouldRefreshOnWake(expiresAt, Date.now())) void load();
    };

    loadRef.current = load;
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
    void load();

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [identity]);

  const value = useMemo(() => ({ credentials, state, refresh }), [credentials, state, refresh]);

  return <SearchKeyContext.Provider value={value}>{children}</SearchKeyContext.Provider>;
}

export const useSearchCredentials = (): SearchCredentialsValue => useContext(SearchKeyContext);
